import { useEffect, useState } from 'react'
import { useVersionCheck } from '../hooks/useVersionCheck.js'

/**
 * UpdateBanner — full-width banner at the top of the dashboard that
 * appears the moment a newer deploy is detected. Clicking "Reload now"
 * does a hard refresh.
 *
 * If the user ignores the banner, we auto-reload after 2 minutes of
 * idle — gentle enough not to interrupt active typing, but strong
 * enough that long-open tabs don't stay stuck on old code.
 *
 * "Idle" = no keystrokes in the last 30 seconds. So a user actively
 * typing a weekly summary won't have their work interrupted; a user
 * who stepped away from their keyboard gets an auto-reload.
 */
export default function UpdateBanner() {
  const { isStale, releaseNote } = useVersionCheck()
  const [dismissed, setDismissed] = useState(false)
  const [lastKey, setLastKey] = useState(Date.now())

  // Track last keystroke so we can reload only when user isn't typing
  useEffect(() => {
    if (!isStale) return
    const onKey = () => setLastKey(Date.now())
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isStale])

  // Auto-reload timer — checks every 10s; reloads when both:
  //   1. banner has been visible for ≥ 2 minutes, AND
  //   2. user hasn't typed in the last 30 seconds
  useEffect(() => {
    if (!isStale || dismissed) return
    const bannerShownAt = Date.now()
    const interval = setInterval(() => {
      const bannerAge = Date.now() - bannerShownAt
      const idleFor = Date.now() - lastKey
      if (bannerAge > 2 * 60 * 1000 && idleFor > 30 * 1000) {
        // eslint-disable-next-line no-undef
        window.location.reload()
      }
    }, 10_000)
    return () => clearInterval(interval)
  }, [isStale, dismissed, lastKey])

  if (!isStale || dismissed) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2000,
        background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)',
        color: '#92400e',
        borderBottom: '1px solid #fbbf24',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span style={{ fontSize: 14 }}>🔄</span>
      <span style={{ flex: 1 }}>
        A new version of the dashboard is live
        {releaseNote ? (
          <>
            : <span style={{ fontWeight: 500 }}>{releaseNote}</span>.{' '}
          </>
        ) : (
          <>. </>
        )}
        Reload to pick it up — otherwise your edits might clash with newer logic.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          background: '#92400e',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          padding: '6px 14px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Reload now
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        title="Hide until next deploy"
        style={{
          background: 'transparent',
          color: '#92400e',
          border: 'none',
          fontSize: 14,
          cursor: 'pointer',
          padding: 4,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  )
}
